/**
 * Cross-stage candidate move shared by every surface that changes a
 * candidate's pipeline stage (Kanban drop today).
 *
 * Owns only the transition decision: blocked → feedback toast; stages backed
 * by a confirmation flow → existing dialogs via `requestMoveToStage`;
 * everything else → optimistic `moveCard`. Dialog state and rendering stay in
 * `useCandidatePipelineMutations` / `CandidatePipelineActionDialogs`.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { Candidate, PipelineStage } from '../models/job';
import type { useCandidatePipelineDnd } from './useCandidatePipelineDnd';
import { useStageDisplayLabel } from './useStageDisplayLabel';
import {
  canMoveCandidateToStage,
  mapDragToPipelineAction,
} from '../utils/candidate-actions';
import {
  applyActionFeedback,
  feedbackForBlockedClientMove,
} from '../utils/pipeline-action-feedback';
import { stageWillNotify } from '../utils/pipeline-stages';

export interface CandidateStageMoveRequestOptions {
  insertAfterCandidateId?: string | null;
}

interface UseCandidateStageMoveInput {
  mutations: {
    requestMoveToStage: (
      candidate: Candidate,
      targetStage: PipelineStage,
      options?: CandidateStageMoveRequestOptions
    ) => boolean;
  };
  pipelineDnd: Pick<ReturnType<typeof useCandidatePipelineDnd>, 'moveCard'>;
}

export interface CandidateStageMoveOptions {
  /** Position of the candidate's current stage (blocks interviewed → earlier). */
  fromPosition?: number;
  /**
   * Candidate to insert after in the destination stage; null = top.
   * Omit to append at the end (no drop position, e.g. a menu action).
   */
  insertAfterCandidateId?: string | null;
  /** Toast the destination when the move itself is not visible (e.g. Table). */
  announceMove?: boolean;
}

export function useCandidateStageMove({
  mutations,
  pipelineDnd,
}: UseCandidateStageMoveInput) {
  const { t } = useTranslation(['candidates']);
  const getStageLabel = useStageDisplayLabel();

  const moveToStage = useCallback(
    (
      candidate: Candidate,
      targetStage: PipelineStage,
      {
        fromPosition,
        insertAfterCandidateId,
        announceMove,
      }: CandidateStageMoveOptions = {}
    ) => {
      const fromType = candidate.pipelineStageSemantic;
      if (
        !canMoveCandidateToStage(fromType, targetStage.stageType, {
          sameStageId: false,
          fromPosition,
          toPosition: targetStage.position,
        })
      ) {
        applyActionFeedback(
          t,
          'candidates:notifications',
          feedbackForBlockedClientMove({
            fromType,
            toType: targetStage.stageType,
            fromPosition,
            toPosition: targetStage.position,
          })
        );
        return;
      }

      // Stage changes that open a dialog keep the candidate in place until confirmed.
      const mapped = mapDragToPipelineAction(
        candidate,
        {
          id: targetStage.id,
          stageType: targetStage.stageType,
          position: targetStage.position,
        },
        fromPosition
      );
      if (mapped.kind !== 'pipeline_move') {
        mutations.requestMoveToStage(candidate, targetStage, {
          insertAfterCandidateId,
        });
        return;
      }

      pipelineDnd.moveCard.mutate({
        candidateId: candidate.id,
        toStageId: targetStage.id,
        toStageType: targetStage.stageType,
        insertAfterCandidateId,
        willNotify: stageWillNotify(targetStage),
        movedToStageLabel: announceMove ? getStageLabel(targetStage) : undefined,
      });
    },
    [t, mutations, pipelineDnd, getStageLabel]
  );

  return { moveToStage };
}
